(() => {
  'use strict';

  function initialize(section) {
    if (section.dataset.roomReady) return;

    section.dataset.roomReady = 'true';

    const configElement = section.querySelector('.cr-config');

    if (!configElement) return;

    const { images, rooms, products } = JSON.parse(configElement.textContent);

    const photo = section.querySelector('.cr-photo');
    const image = section.querySelector('.cr-room-image');
    const markers = section.querySelector('.cr-markers');
    const card = section.querySelector('.cr-card');
    const list = section.querySelector('.cr-products');
    const status = section.querySelector('[role=status]');
    const tabs = [...section.querySelectorAll('.cr-tab')];

    const rows = [];
    const mobile = matchMedia('(max-width:800px)');
    const controller = new AbortController();

    const uid =
      'chesters-room-' +
      (
        window.crypto?.randomUUID?.() ||
        Math.random().toString(36).slice(2)
      );

    card.id = uid;

    let roomIndex = 0;
    let active = -1;
    let opener = null;
    let buttons = new Map();


    /* ================================
       POSITION PRODUCT INFORMATION CARD
    ================================= */

    function positionCard() {
      if (
        active < 0 ||
        mobile.matches ||
        card.hidden
      ) {
        return;
      }

      const product = products[active];

      const width = photo.clientWidth;
      const height = photo.clientHeight;

      const cardWidth = card.offsetWidth;
      const cardHeight = card.offsetHeight;

      const x = width * product.x / 100;
      const y = height * product.y / 100;

      let left = x + 26;

      if (left + cardWidth > width - 12) {
        left = x - cardWidth - 26;
      }

      card.style.left =
        Math.max(
          12,
          Math.min(left, width - cardWidth - 12)
        ) + 'px';

      card.style.top =
        Math.max(
          12,
          Math.min(
            y - cardHeight / 2,
            height - cardHeight - 12
          )
        ) + 'px';
    }


    /* ================================
       CLOSE PRODUCT CARD
    ================================= */

    function close(restore = false) {
      const target = opener;

      active = -1;

      card.hidden = true;

      buttons.forEach(button => {
        button.setAttribute(
          'aria-expanded',
          'false'
        );
      });

      rows.forEach(row => {
        row.classList.remove('is-active');
      });

      if (
        restore &&
        target?.isConnected
      ) {
        target.focus();
      }
    }


    /* ================================
       SHOW ROOM
    ================================= */

    function showRoom(index, announce = true) {
      close();

      roomIndex =
        (index + rooms.length) %
        rooms.length;

      const room = rooms[roomIndex];

      image.src = images[room.id];
      image.alt = room.alt;

      image.width = room.imageWidth;
      image.height = room.imageHeight;

      image.style.setProperty(
        '--image-width',
        room.width
      );

      image.style.setProperty(
        '--image-left',
        room.left
      );

      image.style.setProperty(
        '--image-top',
        room.top
      );


      /* Update tabs */

      tabs.forEach(tab => {
        tab.setAttribute(
          'aria-pressed',
          String(
            tab.dataset.room === room.id
          )
        );
      });


      /* Clear old markers */

      markers.replaceChildren();

      buttons = new Map();


      /* Create room hotspots */

      products.forEach(
        (product, index) => {

          const inRoom =
            product.room === room.id;

          if (rows[index]) {
            rows[index].classList.toggle(
              'is-in-room',
              inRoom
            );

            const location =
              rows[index].querySelector(
                '.cr-location'
              );

            if (location) {
              const productRoom =
                rooms.find(
                  item =>
                    item.id ===
                    product.room
                );

              location.textContent =
                inRoom
                  ? 'In this view'
                  : productRoom?.name || '';
            }
          }

          if (!inRoom) return;


          /* Create hotspot */

          const button =
            document.createElement(
              'button'
            );

          button.type = 'button';

          button.className =
            'cr-hotspot';

          button.style.setProperty(
            '--x',
            product.x + '%'
          );

          button.style.setProperty(
            '--y',
            product.y + '%'
          );

          button.setAttribute(
            'aria-label',
            'Explore ' + product.name
          );

          button.setAttribute(
            'aria-expanded',
            'false'
          );

          button.setAttribute(
            'aria-controls',
            uid
          );


          const plus =
            document.createElement(
              'span'
            );

          plus.textContent = '+';

          plus.setAttribute(
            'aria-hidden',
            'true'
          );

          button.append(plus);


          button.addEventListener(
            'click',
            () =>
              openProduct(
                index,
                button
              )
          );


          button.addEventListener(
            'pointerenter',
            event => {

              if (
                event.pointerType ===
                  'mouse' &&
                !card.contains(
                  document.activeElement
                )
              ) {
                openProduct(
                  index,
                  button
                );
              }
            }
          );


          button.addEventListener(
            'keydown',
            event => {

              if (
                event.key ===
                'ArrowDown'
              ) {
                event.preventDefault();

                openProduct(
                  index,
                  button
                );

                card
                  .querySelector('a')
                  ?.focus();
              }
            }
          );


          markers.append(button);

          buttons.set(
            index,
            button
          );
        }
      );


      /* Count room collections */

      const count =
        products.filter(
          product =>
            product.room === room.id
        ).length;


      /*
       * These elements aren't currently
       * included in the Liquid markup.
       * Check for them before updating.
       */

      const caption =
        section.querySelector(
          '.cr-room-caption'
        );

      if (caption) {
        caption.textContent =
          room.name +
          ' · ' +
          count +
          ' collection' +
          (count === 1 ? '' : 's');
      }


      const countElement =
        section.querySelector(
          '.cr-count'
        );

      if (countElement) {
        countElement.textContent =
          String(
            roomIndex + 1
          ).padStart(2, '0') +
          ' / ' +
          String(
            rooms.length
          ).padStart(2, '0');
      }


      if (
        announce &&
        status
      ) {
        status.textContent =
          room.name +
          ' view. ' +
          count +
          ' furniture collection' +
          (count === 1 ? '' : 's') +
          '.';
      }
    }


    /* ================================
       OPEN PRODUCT
    ================================= */

    function openProduct(
      index,
      trigger
    ) {

      const product =
        products[index];


      if (
        rooms[roomIndex].id !==
        product.room
      ) {

        showRoom(
          rooms.findIndex(
            room =>
              room.id ===
              product.room
          ),
          false
        );
      }


      active = index;

      opener =
        trigger ||
        buttons.get(index);


      const title =
        card.querySelector('h3');

      const description =
        card.querySelector('p');

      const link =
        card.querySelector('a');


      if (title) {
        title.textContent =
          product.name;
      }

      if (description) {
        description.textContent =
          product.detail;
      }


      if (link) {
        link.href =
          product.url;

        link.textContent =
          'View Collection →';

        link.setAttribute(
          'aria-label',
          'View ' +
            product.name +
            ' collection'
        );
      }


      buttons.forEach(
        (
          button,
          buttonIndex
        ) => {

          button.setAttribute(
            'aria-expanded',
            String(
              buttonIndex ===
              index
            )
          );
        }
      );


      rows.forEach(
        (
          row,
          rowIndex
        ) => {

          row.classList.toggle(
            'is-active',
            rowIndex === index
          );
        }
      );


      card.hidden = false;

      positionCard();


      if (status) {
        status.textContent =
          product.name +
          '. ' +
          product.detail;
      }
    }


    /* ================================
       BUILD PRODUCT LIST
    ================================= */

    products.forEach(
      (product, index) => {

        const row =
          document.createElement(
            'li'
          );

        row.className =
          'cr-product';


        const thumb =
          document.createElement(
            'button'
          );

        thumb.type = 'button';

        thumb.className =
          'cr-thumb';

        const productRoom =
          rooms.find(
            room =>
              room.id ===
              product.room
          );


        thumb.setAttribute(
          'aria-label',
          'Show ' +
            product.name +
            ' in ' +
            (
              productRoom?.name ||
              ''
            )
        );


        /*
         * Desktop crop settings.
         * Mobile CSS overrides these
         * so images can't disappear.
         */

        thumb.style.setProperty(
          '--zoom',
          product.zoom
        );

        thumb.style.setProperty(
          '--left',
          product.left
        );

        thumb.style.setProperty(
          '--top',
          product.top
        );


        const img =
          document.createElement(
            'img'
          );

        img.src =
          images[product.room];

        img.alt = '';

        img.loading = 'lazy';

        img.decoding = 'async';

        thumb.append(img);


        thumb.addEventListener(
          'click',
          () => {

            openProduct(
              index,
              thumb
            );

            if (mobile.matches) {
              section
                .querySelector(
                  '.cr-visual'
                )
                ?.scrollIntoView({
                  behavior: 'auto',
                  block: 'start'
                });
            }
          }
        );


        const copy =
          document.createElement(
            'div'
          );

        const location =
          document.createElement(
            'p'
          );

        const name =
          document.createElement(
            'h4'
          );

        const link =
          document.createElement(
            'a'
          );


        copy.className =
          'show_the_room_product_copy';

        location.className =
          'cr-location';

        name.className =
          'cr-product-name';

        name.textContent =
          product.name;


        link.className =
          'cr-link';

        link.href =
          product.url;

        link.textContent =
          'View Collection →';

        link.setAttribute(
          'aria-label',
          'View ' +
            product.name +
            ' collection'
        );


        copy.append(
          location,
          name,
          link
        );

        row.append(
          thumb,
          copy
        );

        list.append(row);

        rows.push(row);
      }
    );


    /* ================================
       TAB EVENTS
    ================================= */

    tabs.forEach(
      (tab, index) => {

        tab.addEventListener(
          'click',
          () =>
            showRoom(index),
          {
            signal:
              controller.signal
          }
        );


        tab.addEventListener(
          'keydown',
          event => {

            let next;


            if (
              event.key ===
              'ArrowRight'
            ) {
              next =
                (index + 1) %
                tabs.length;
            }

            else if (
              event.key ===
              'ArrowLeft'
            ) {
              next =
                (
                  index +
                  tabs.length -
                  1
                ) %
                tabs.length;
            }

            else if (
              event.key ===
              'Home'
            ) {
              next = 0;
            }

            else if (
              event.key ===
              'End'
            ) {
              next =
                tabs.length - 1;
            }

            else {
              return;
            }


            event.preventDefault();

            showRoom(next);

            tabs[next].focus();

          },
          {
            signal:
              controller.signal
          }
        );
      }
    );


    /* ================================
       DIRECTION BUTTONS
    ================================= */

    section
      .querySelectorAll(
        '[data-direction]'
      )
      .forEach(button => {

        button.addEventListener(
          'click',
          () =>
            showRoom(
              roomIndex +
                Number(
                  button.dataset
                    .direction
                )
            ),
          {
            signal:
              controller.signal
          }
        );
      });


    /* ================================
       CLOSE BUTTON
    ================================= */

    card
      .querySelector(
        '.cr-close'
      )
      ?.addEventListener(
        'click',
        () => close(true),
        {
          signal:
            controller.signal
        }
      );


    /* ================================
       ESCAPE KEY
    ================================= */

    section.addEventListener(
      'keydown',
      event => {

        if (
          event.key ===
            'Escape' &&
          active >= 0
        ) {

          event.preventDefault();

          close(true);
        }
      },
      {
        signal:
          controller.signal
      }
    );


    /* ================================
       OUTSIDE CLICK
    ================================= */

    document.addEventListener(
      'pointerdown',
      event => {

        if (
          !section.contains(
            event.target
          )
        ) {
          close();
        }
      },
      {
        signal:
          controller.signal
      }
    );


    /* ================================
       RESIZE
    ================================= */

    const observer =
      new ResizeObserver(
        positionCard
      );

    observer.observe(photo);


    mobile.addEventListener(
      'change',
      positionCard
    );


    image.addEventListener(
      'load',
      positionCard,
      {
        signal:
          controller.signal
      }
    );


    /* ================================
       CLEANUP
    ================================= */

    section.roomCleanup =
      () => {

        observer.disconnect();

        controller.abort();

        mobile.removeEventListener(
          'change',
          positionCard
        );

        delete section.dataset
          .roomReady;

        list.replaceChildren();
      };


    /* Initial room */

    showRoom(0, false);
  }


  /* ================================
     INITIALIZE
  ================================= */

  function init(root = document) {

    if (
      root.matches?.(
        '[data-chesters-room]'
      )
    ) {
      initialize(root);
    }


    root
      .querySelectorAll(
        '[data-chesters-room]'
      )
      .forEach(
        initialize
      );
  }


  init();


  /* ================================
     SHOPIFY THEME EDITOR
  ================================= */

  if (
    !window.chestersRoomLifecycle
  ) {

    window.chestersRoomLifecycle =
      true;


    document.addEventListener(
      'shopify:section:load',
      event =>
        init(event.target)
    );


    document.addEventListener(
      'shopify:section:unload',
      event => {

        if (
          event.target.matches?.(
            '[data-chesters-room]'
          )
        ) {
          event.target
            .roomCleanup?.();
        }


        event.target
          .querySelectorAll(
            '[data-chesters-room]'
          )
          .forEach(
            section =>
              section
                .roomCleanup?.()
          );
      }
    );
  }
})();